import type { Dispatch, FC } from "react"
import React from "react"
import { Flex, Stack } from "@chakra-ui/react"
import { FieldArray, useFormikContext } from "formik"
import invariant from "tiny-invariant"

import { useAddress } from "web3/hooks/useAddress"
import ActionForm from "createProposal/components/actions/ActionForm"
import type { Governor, GovernorContract } from "query/graphql"
import { ExecutableCallType } from "query/graphql"
import type {
  CreateProposalFormState,
  CreateProposalFormValues,
} from "createProposal/types/form"
import { StepName } from "createProposal/types/form"
import EmptyAction from "createProposal/action/EmptyAction"

type ActionProps = {
  state: Pick<CreateProposalFormState, "currentStep">
  timelockId: string | undefined | null
  index: number
  setActionIndex: Dispatch<React.SetStateAction<number>>
  chainId: string
  governance: Pick<Governor, "id"> & {
    contracts: {
      governor: Pick<GovernorContract, "address" | "type">
    }
  }
}

const CreateProposalAction: FC<ActionProps> = ({
  state,
  setActionIndex,
  index,
  timelockId,
  chainId,
}) => {
  const { currentStep } = state

  const isActive = currentStep === StepName.ACTIONS

  const address = useAddress()

  const { values, setFieldValue, validateForm, setFieldTouched } =
    useFormikContext<CreateProposalFormValues>()

  return (
    <Flex
      display={isActive ? "" : "none"}
      mt={0}
      p={2}
      pt={{ base: 6, sm: 2 }}
      width="100%"
    >
      <FieldArray
        name="actions"
        render={({}) => {
          return values?.actions?.map((action, idx) => {
            const isActive = idx === index

            const { recipe } = action

            const isRemoveActionDisabled =
              index === 0 &&
              (recipe === null || recipe === ExecutableCallType.Empty)

            const handleRemoveActionClick = () => {
              if (index < 0) {
                return
              }

              invariant(address, "`address` must be defined")

              // This is the only action, so we reset the form
              if (values.actions?.length === 1) {
                setFieldValue("actions", [
                  new EmptyAction({ targetAddress: address }),
                ])

                setFieldTouched(`actions`, false, false)

                // Wait for the form to update before validating
                setTimeout(() => {
                  validateForm()
                }, 300)

                return
              }

              if (index === 0) {
                const newActions = values.actions?.filter(
                  (_, idx) => index !== idx,
                )

                setFieldValue("actions", newActions)

                setActionIndex(index)

                setFieldTouched(`actions.${index}`, false, false)
                setFieldTouched(`actions.${index}.targetAddress`, false, false)

                // Wait for the form to update before validating
                setTimeout(() => {
                  validateForm()
                }, 300)

                return
              }

              // if last index, lower the index after removing
              if (index === (values?.actions?.length ?? 0) - 1) {
                const newActions = values.actions?.filter(
                  (_, idx) => index !== idx,
                )

                setFieldValue("actions", newActions)

                setActionIndex(index - 1)

                setFieldTouched(`actions.${index - 1}`, false, false)
                setFieldTouched(
                  `actions.${index - 1}.targetAddress`,
                  false,
                  false,
                )

                // Wait for the form to update before validating
                setTimeout(() => {
                  validateForm()
                }, 300)
              }

              // Otherwise, remove the action and update the index
              else {
                const newActions = values.actions?.filter(
                  (_, idx) => index !== idx,
                )

                setFieldValue("actions", newActions)

                setActionIndex(index)

                setFieldTouched(`actions.${index}`, false, false)
                setFieldTouched(`actions.${index}.targetAddress`, false, false)

                // Wait for the form to update before validating
                setTimeout(() => {
                  validateForm()
                }, 300)
              }
            }

            return (
              <Stack
                key="idx"
                display={isActive ? "flex" : "none"}
                spacing={6}
                w="full"
              >
                <Stack spacing={8} w="full">
                  <ActionForm
                    key={idx}
                    action={action}
                    chainId={chainId}
                    handleRemoveActionClick={handleRemoveActionClick}
                    index={idx}
                    isRemoveActionDisabled={isRemoveActionDisabled}
                    timelockId={timelockId}
                  />
                </Stack>
              </Stack>
            )
          })
        }}
        validateOnChange={false}
      />
    </Flex>
  )
}

export default CreateProposalAction
